import { useEffect, useState } from "react";
import { Download, Check, X, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiError } from "../api";
import { PROPOSAL_STATUS_LABELS } from "../lib/constants";

export default function InlenerDashboard() {
  const [profile, setProfile] = useState(null);
  const [props, setProps] = useState([]);

  const load = async () => {
    const [p, pr] = await Promise.all([
      api.get("/profile/client"),
      api.get("/proposals/mine"),
    ]);
    setProfile(p.data);
    setProps(pr.data);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const mark = async (pid, status) => {
    try {
      await api.put(`/proposals/${pid}/feedback`, { status, client_feedback: "" });
      toast.success("Markering opgeslagen.");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!profile) return <div className="ok-container py-20 text-sm">Laden…</div>;

  return (
    <section className="py-12">
      <div className="ok-container">
        <div className="ok-eyebrow mb-2">Inlener-dashboard</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight" data-testid="client-dash-title">
          {profile.organization_name}
        </h1>
        <p className="text-[#4a5d5e] mt-2 text-sm">Contactpersoon: {profile.contact_name} ({profile.email})</p>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-4 gap-5">
          <Stat label="Voorstellen" value={props.length} />
          <Stat label="Interessant" value={props.filter((p) => p.status === "interessant").length} />
          <Stat label="Niet passend" value={props.filter((p) => p.status === "niet_passend").length} />
          <Stat label="Open voorstellen" value={props.filter((p) => p.status === "verstuurd").length} />
        </div>

        <h2 className="font-display text-xl font-semibold mt-12 mb-4">Voorgestelde kandidaten</h2>
        {props.length === 0 ? (
          <p className="text-sm text-[#7b8e8f] border border-dashed border-[#e2e8f0] p-8 text-center">
            Nog geen voorstellen. Onze recruiters nemen contact op zodra wij een passende kandidaat hebben.
          </p>
        ) : (
          <div className="space-y-4">
            {props.map((p) => (
              <div key={p.id} className="border border-[#e2e8f0] p-6" data-testid={`proposal-${p.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="ok-chip mb-2">{PROPOSAL_STATUS_LABELS[p.status]}</div>
                    <h3 className="font-display text-lg font-semibold">
                      {p.candidate?.first_name} {p.candidate?.last_name}
                    </h3>
                    <div className="text-xs text-[#4a5d5e] mt-1">
                      {p.candidate?.expertise_category} · {p.candidate?.years_experience} jaar · € {p.candidate?.hourly_rate || "n.v.t."}/u · beschikbaar {p.candidate?.available_from || "n.v.t."}
                    </div>
                    {p.vacancy && <div className="text-xs mt-2">Voor vacature: <strong>{p.vacancy.title}</strong></div>}
                    {p.candidate?.motivation && (
                      <p className="text-sm text-[#0a191a] mt-3 max-w-xl">{p.candidate.motivation}</p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 min-w-[180px]">
                    {p.candidate?.has_resume && (
                      <a
                        data-testid={`download-cv-${p.id}`}
                        href={`${API}/resume/download/${p.candidate.id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center justify-center gap-2 border border-[#0a191a] py-2 rounded-sm text-sm hover:bg-[#0a191a] hover:text-white"
                      >
                        <Download size={14} /> CV downloaden
                      </a>
                    )}
                    {p.status === "verstuurd" && (
                      <>
                        <button data-testid={`mark-interesting-${p.id}`} onClick={() => mark(p.id, "interessant")}
                          className="inline-flex items-center justify-center gap-2 bg-[#01696f] text-white py-2 rounded-sm text-sm hover:bg-[#015257]">
                          <Check size={14} /> Interessant
                        </button>
                        <button data-testid={`mark-not-suitable-${p.id}`} onClick={() => mark(p.id, "niet_passend")}
                          className="inline-flex items-center justify-center gap-2 border border-[#d32f2f] text-[#d32f2f] py-2 rounded-sm text-sm hover:bg-[#d32f2f] hover:text-white">
                          <X size={14} /> Niet passend
                        </button>
                      </>
                    )}
                    <a href="mailto:info@overheidskrachten.nl"
                      className="inline-flex items-center justify-center gap-2 text-xs text-[#4a5d5e] hover:text-[#01696f]">
                      <Mail size={12} /> Recruiter contacten
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-[#e2e8f0] p-5">
      <div className="text-xs uppercase tracking-wider text-[#4a5d5e]">{label}</div>
      <div className="font-display text-3xl font-bold text-[#01696f] mt-2">{value}</div>
    </div>
  );
}
